/**
 * ProtectedRoute.tsx — D.4.2 Authentication and Role-Based Route Guard
 *
 * Used as a layout route in react-router-dom v6:
 *
 *   <Route element={<ProtectedRoute />}>
 *     <Route path="/dashboard" element={<DashboardPage />} />
 *   </Route>
 *
 *   <Route element={<ProtectedRoute requiredRoles={['platform_admin']} />}>
 *     <Route path="/admin" element={<AdminPage />} />
 *   </Route>
 *
 * Guard logic (evaluated in order):
 *   1. isLoading → show <LoadingScreen /> (prevents flash of login page)
 *   2. not authenticated → <Navigate to="/login" state={{ from: location }} />
 *   3. authenticated + requiredRoles provided + user lacks role → <Navigate to="/unauthorized" />
 *   4. platform_admin → always bypasses role check (superuser)
 *   5. all checks passed → render <Outlet />
 *
 * Role is read from `user.appRole` — a custom property attached by the auth
 * service when it resolves the Firebase user (populated from a custom claim or
 * Firestore profile lookup).
 */
import { useLocation, Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { LoadingScreen } from '@/pages/LoadingScreen';
import type { UserRole } from '@philofund/shared/types/user';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ProtectedRouteProps {
  /**
   * If provided, the authenticated user must have one of these roles to
   * access the route. When omitted, any authenticated user is allowed.
   * platform_admin always bypasses this check.
   */
  requiredRoles?: UserRole[];
}

// ---------------------------------------------------------------------------
// Helper: extract role from the user object stored in the auth store.
// The Firebase User object does not carry a role natively — it is attached
// as a custom `appRole` property by the auth service.
// ---------------------------------------------------------------------------

function getUserRole(user: unknown): UserRole | undefined {
  if (
    user !== null &&
    typeof user === 'object' &&
    'appRole' in user &&
    typeof (user as Record<string, unknown>)['appRole'] === 'string'
  ) {
    return (user as Record<string, unknown>)['appRole'] as UserRole;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProtectedRoute({ requiredRoles }: ProtectedRouteProps): JSX.Element {
  const { user, isLoading } = useAuthStore();
  const location = useLocation();

  // 1. Auth state is still being resolved — show loading screen
  if (isLoading) {
    return <LoadingScreen />;
  }

  // 2. Not authenticated — redirect to login, preserving intended destination
  if (user === null) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 3. Role check (only when requiredRoles is specified).
  //
  // SEGREGATION OF DUTIES (PRD-021): there is deliberately NO platform_admin
  // bypass here. A platform admin operates cross-tenant surfaces (/admin/*)
  // and must not silently enter donor or org-admin surfaces — mirroring the
  // backend, where IsPlatformAdmin and IsTenantMember are disjoint DRF guards
  // and tenant rows are RLS-scoped. Each route family names its role
  // explicitly; wrong-role access lands on /unauthorized.
  if (requiredRoles !== undefined && requiredRoles.length > 0) {
    const role = getUserRole(user);
    const hasRequiredRole = role !== undefined && requiredRoles.includes(role);
    if (!hasRequiredRole) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // 4. All checks passed — render the child route
  return <Outlet />;
}
